import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { 
  Box, 
  Grid, 
  TextField, 
  Button, 
  Typography, 
  Collapse, 
  Switch,  
  CircularProgress, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  FormHelperText,
  Divider,
  Tooltip
} from "@material-ui/core";
import { Formik, Form, FieldArray, Field, useFormikContext } from "formik";
import * as Yup from "yup";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import TemplateViewer from "../../components/TemplateViewer";
import { format } from "date-fns";
import { generateFormConfig, hydrateCraftTemplateClient, generateMedicationsHtml } from "../../helpers/prescriptionUtils";
import html2canvas from "html2canvas";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import DescriptionIcon from "@material-ui/icons/Description";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { useParams, useHistory, useLocation } from "react-router-dom"; 
import toastError from "../../errors/toastError";
import jsPDF from 'jspdf';
import { useTitle } from "../../hooks/useTitle";

const AutoPreviewWatcher = ({ onChange }) => {
  const { values } = useFormikContext();
  useEffect(() => {
    if (!values) return;
    onChange(values);
  }, [values, onChange]);
  return null;
};

const FieldsUpdater = ({ doctorTitles }) => {
  const { values, setFieldValue } = useFormikContext();
  const { especialidadMedico } = values;
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (especialidadMedico && doctorTitles.length > 0) {
      const selectedTitleObject = doctorTitles.find(t => t.title === especialidadMedico);
      
      if (selectedTitleObject && selectedTitleObject.DoctorProfessionalTitle) {
        const newCedula = selectedTitleObject.DoctorProfessionalTitle.professionalLicense;
        setFieldValue('cedulaMedico', newCedula);
      } else {
        setFieldValue('cedulaMedico', '');
      }
    }

  }, [especialidadMedico, doctorTitles, setFieldValue]);

  return null; 
};

const NewPrescription = () => {
  const { templateId, contactId } = useParams();
  const { user } = useContext(AuthContext);
  const location = useLocation(); 
  const sigCanvas = useRef();
  const history = useHistory();
  const initialPatientName = location.state?.patientName || "Paciente";
  useTitle("Nueva Receta para " + initialPatientName);
  const [template, setTemplate] = useState(null);
  const [contact, setContact] = useState(null);
  const [offscreenHydratedJson, setOffscreenHydratedJson] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [doctorTitles, setDoctorTitles] = useState([]);
  const [selectedTitleName, setSelectedTitleName] = useState("");
  const [company, setCompany] = useState(null);
  const formikRef = useRef();
  const [loading, setLoading] = useState(true);
  const [lastAppointment, setLastAppointment] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState(null);
  const [createdPdfUrl, setCreatedPdfUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureOption, setSignatureOption] = useState('manual');
  const [shouldCompleteAppointment, setShouldCompleteAppointment] = useState(true);
  
  const [observationComment, setObservationComment] = useState("");
  const [sendingObservation, setSendingObservation] = useState(false);

  useEffect(() => {
    const fetchTemplateAndContact = async () => {
      try {
        const [templateRes, contactRes] = await Promise.all([
          api.get("/prescriptionTemplates/" + templateId),
          api.get("/contacts/" + contactId)
        ]);

        setTemplate(templateRes.data);
        setContact(contactRes.data);
      } catch (err) {
        console.error("Error cargando template o contacto:", err);
        toast.error("No se pudieron cargar los datos de la receta");
      }
    };

    if (templateId && contactId) fetchTemplateAndContact();
  }, [templateId, contactId]);

  useEffect(() => {
    const fetchAllInitialData = async () => {
      try {
        if (!user?.id || !contactId) return;

        const queryParams = new URLSearchParams(location.search);
        const appointmentIdFromQuery = queryParams.get('appointmentId');
        
        let lastAppointmentPromise;

        if (appointmentIdFromQuery) {
          lastAppointmentPromise = api.get("/appointments/" + appointmentIdFromQuery).catch(err => {
            console.warn("No se encontró la cita específica con ID " + appointmentIdFromQuery, err);
            return { data: null };
          });
        } else {
          const patientNameFromState = location.state?.patientName;
          const lastAppointmentParams = {};
          if (patientNameFromState) {
            lastAppointmentParams.patientName = patientNameFromState;
          }
          lastAppointmentPromise = api.get("/appointments/last/" + contactId, { params: lastAppointmentParams }).catch(err => {
            console.warn("No se encontró una cita previa para este paciente.", err);
            return { data: null };
          });
        }

        const [companyRes, userRes, contactRes, lastAppointmentRes] = await Promise.all([
          api.get("/companies/" + user.companyId),
          api.get("/users/" + user.id),
          api.get("/contacts/" + contactId),
          lastAppointmentPromise
        ]);
        
        setCompany(companyRes.data);
        setContact(contactRes.data);
        setLastAppointment(lastAppointmentRes.data);
        
        if (userRes.data && userRes.data.signature) {
          setSignatureOption('saved');
        }

        const titles = userRes.data.professionalTitles || [];
        setDoctorTitles(titles);
        
        if (lastAppointmentRes.data && lastAppointmentRes.data.professionalTitleId) {
          const lastTitleObject = titles.find(t => t.id === lastAppointmentRes.data.professionalTitleId);
          if (lastTitleObject) {
            setSelectedTitleName(lastTitleObject.title);
          }
        } else if (titles.length === 1) {
          setSelectedTitleName(titles[0].title);
        }

      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllInitialData();
  }, [user, contactId, location.search, location.state]); 
  
  const { initialValues: dynamicInitialValues, validationSchema: dynamicValidationSchema } =
    generateFormConfig(template?.variables);

  const handlePreviewReady = useCallback(() => {
      console.log("Previsualización DOM lista y ajustada.");
  }, []);
                       
  const generateInitialValues = () => {
    if (!template || !contact || !company || !user) {
      return {};
    }

    const initialValues = { comentarios: '' };
    
    const preloadedData = {
      nombrePaciente: lastAppointment?.patientName || contact?.name || '',
      edadPaciente: lastAppointment?.edad || '',
      fechaConsulta: lastAppointment?.date ? format(new Date(lastAppointment.date), 'dd/MM/yyyy') : format(new Date(), 'dd/MM/yyyy'),
      presionArterial: lastAppointment?.presionArterial || '',
      frecuenciaCardiaca: lastAppointment?.frecuenciaCardiaca || '',
      frecuenciaRespiratoria: lastAppointment?.frecuenciaRespiratoria || '',
      glucemiaCapilar: lastAppointment?.glucemiaCapilar || '',
      temperatura: lastAppointment?.temperatura || '',
      saturacionOxigeno: lastAppointment?.saturacionOxigeno || '',
      talla: lastAppointment?.talla || '',
      peso: lastAppointment?.peso || '',
      alergias: lastAppointment?.alergias || '',
      motivoConsulta: lastAppointment?.motivoConsulta || '',
      nombreMedico: user.name || '',
      nombreClinica: company?.name || '',
      telefonoClinica: company?.phone || '',
      direccionClinica: [company.street, company.city, company.state].filter(Boolean).join(', '),
      fechaEmision: format(new Date(), 'dd/MM/yyyy'),
      cedulaMedico: lastAppointment?.doctorProfessionalTitle?.professionalLicense || '',
    };
      
    template.variables.forEach(variable => {
      initialValues[variable] = preloadedData[variable] || '';
    });
    
    if (template.variables.includes('listaMedicamentos')) {
      initialValues.listaMedicamentos = [{ name: "", dose: "", instructions: "" }];
    }
    if (template.variables.includes('especialidadMedico')) {
      initialValues.especialidadMedico = selectedTitleName; 
    }
    if (template.content?.includes('"Signature"')) {
      initialValues.signature = "";
      initialValues.signatureOption = 'manual';
    }

    return initialValues;
  };

  const PrescriptionSchema = Yup.object().shape({
    ...dynamicValidationSchema,
    especialidadMedico: template?.variables.includes('especialidadMedico')
      ? Yup.string().required("Debes seleccionar una especialidad")
      : Yup.string().optional(),
    listaMedicamentos: template?.variables.includes('listaMedicamentos')
      ? Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required("Requerido"),
            dose: Yup.string().required("Requerido"),
            instructions: Yup.string().required("Requerido"),
          })
        ).min(1, "Agrega al menos un medicamento")
      : Yup.array().optional(),
    signature: Yup.string().when("signatureOption", {
      is: 'draw',
      then: schema => schema.required("La firma es obligatoria para esta opción."),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const handlePreview = useCallback(async (values) => {
    if (!values || !template) return;
    setPreviewLoading(true);
    try {
      const safeValues = { ...values };
      if (values.listaMedicamentos) {
        safeValues.listaMedicamentos = { 
            text: generateMedicationsHtml(values.listaMedicamentos, values.comentarios), 
            allowHtml: true 
        };
    }

      let finalSignatureUrl = null;

      if (signatureOption === 'draw' && sigCanvas.current && !sigCanvas.current.isEmpty()) {
        finalSignatureUrl = sigCanvas.current.getCanvas().toDataURL("image/png");
      } else if (signatureOption === 'saved' && user.signatureUrl) {
        finalSignatureUrl = user.signatureUrl;
      }
      
      safeValues.signatureUrl = finalSignatureUrl;
      
      const hydrated = hydrateCraftTemplateClient(template.content, safeValues);
      setOffscreenHydratedJson(hydrated);
    } catch (err) {
      console.error(err);
      toast.error("Error generando previsualización");
    } finally {
      setPreviewLoading(false);
    }
  }, [template, user, signatureOption]);

  const handleOpenMedicalHistory = () => {
    if (!contactId) return;
    
    const nameToUse = (lastAppointment && lastAppointment.patientName) || (contact && contact.name) || "";
    
    const baseUrl = "/medical-history/" + contactId;
    
    const url = nameToUse 
      ? baseUrl + "?patientName=" + encodeURIComponent(nameToUse) 
      : baseUrl;

    window.open(url, "_blank");
  };

  const handleSendQuickObservation = async () => {
    if (!observationComment.trim()) return;

    try {
      setSendingObservation(true);
      const nameToUse = lastAppointment?.patientName || contact?.name || null;

      await api.post(`/medical-history/patients/${contactId}/observations`, {
        comment: observationComment,
        patientName: nameToUse,
      });

      toast.success("Observación agregada al expediente");
      setObservationComment("");
    } catch (err) {
      toastError(err);
    } finally {
      setSendingObservation(false);
    }
  };

  const handleSubmit = async (values) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const el = document.getElementById("template-craft-viewer");
      if (!el) {
        toast.error("No se encontró el contenido de la receta para capturar");
        setIsSubmitting(false);
        return;
      }

      const signatures = el.querySelectorAll('.signature-image-element');
      signatures.forEach(img => img.style.opacity = '0'); 

      const canvasClean = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null });
      
      signatures.forEach(img => img.style.opacity = '1');

      const imgDataClean = canvasClean.toDataURL("image/png");
      
      const imgWidth = canvasClean.width;
      const imgHeight = canvasClean.height;
      
      const pdfClean = new jsPDF({
          orientation: imgWidth > imgHeight ? 'l' : 'p',
          unit: 'px',
          format: [imgWidth, imgHeight]
      });

      pdfClean.addImage(imgDataClean, 'PNG', 0, 0, imgWidth, imgHeight);
      
      const pdfBlob = pdfClean.output('blob');
      const pdfUrlLocal = URL.createObjectURL(pdfBlob);
      window.open(pdfUrlLocal, '_blank');

      let signatureUrl = null;
      if (signatureOption === 'draw' && sigCanvas.current && !sigCanvas.current.isEmpty()) {
        signatureUrl = sigCanvas.current.toDataURL("image/png");
      } else if (signatureOption === 'saved' && user.signature) {
        signatureUrl = user.signature;
      }

      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null });
      const imgData = canvas.toDataURL("image/png");
      const res = await fetch(imgData);
      const blob = await res.blob();
      const file = new File([blob], `receta-${Date.now()}.png`, { type: "image/png" });

      const formData = new FormData();
      formData.append("templateId", String(template.id));
      formData.append("contactId", String(contact.id));
      if (signatureUrl) {
        formData.append("signatureUrl", signatureUrl);
      }
      formData.append("content", JSON.stringify(values));
      formData.append("imageFile", file);
      
      if (shouldCompleteAppointment && lastAppointment) {
        formData.append("completeAppointment", "true");
        formData.append("appointmentId", lastAppointment.id);
      }
      
      const { data } = await api.post("/prescriptions", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (data.messageError) {
          toast.warn(data.messageError, { autoClose: 10000 });
      } else {
          toast.success("Receta creada y enviada correctamente");
      }

      setCreatedTicketId(data.ticketId);
      setCreatedPdfUrl(data.pdfUrl);
      
      setOpenDialog(true);

    } catch (err) {
      console.error(err);
      toastError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const EmptyFieldsWarning = () => {
    const { values, submitCount } = useFormikContext();
    const [showWarning, setShowWarning] = useState(false);
    const [warningCount, setWarningCount] = useState(0);

    const variablesToCheck = template?.variables.filter(v => 
      !['listaMedicamentos', 'signature', 'especialidadMedico'].includes(v)) || [];

    useEffect(() => {
      let emptyCount = 0;
      variablesToCheck.forEach(v => {
        if (!values[v] || (typeof values[v] === 'string' && values[v].trim() === '')) {
          emptyCount++;
        }
      });

      setWarningCount(emptyCount);

      if (submitCount > 0) {
          setShowWarning(emptyCount > 0);
      }

    }, [values, variablesToCheck, submitCount]);

    if (warningCount > 0) {
      return (
        <Box mt={2} p={1} bgcolor="#ffeb3b" color="#333" borderRadius={4} border="1px solid #fdd835">
          <Typography variant="caption" style={{ fontWeight: 'bold' }}>
            ⚠️ ADVERTENCIA: Hay {warningCount} campo(s) vacío(s) que aparecerán como "N/A" en la receta.
          </Typography>
        </Box>
      );
    }
    return null;
  }

  if (!template || !contact) {
    return (
      <Box p={4}>
        <Typography>Cargando datos de la receta...</Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" height="calc(100vh - 48px)">
      <Box width="50%" overflow="auto" p={2}>
        <Box mb={3} p={2} border={1} borderColor="divider" borderRadius={8} bgcolor="background.paper" boxShadow={1}>
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, opacity: 0.8 }}>
            Acciones de Historia Clínica
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Button 
                variant="outlined" 
                color="primary" 
                size="small"
                onClick={handleOpenMedicalHistory}
                startIcon={<DescriptionIcon />}
              >
                Ver Expediente Médico
              </Button>
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" gap={1} alignItems="stretch">
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Agregar observación rápida al expediente..."
                  value={observationComment}
                  onChange={(e) => setObservationComment(e.target.value)}
                  disabled={sendingObservation}
                  multiline
                  maxRows={2}
                />
                <Tooltip title="Guardar Observación">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendQuickObservation}
                    disabled={sendingObservation || !observationComment.trim()}
                    style={{ minWidth: 50, padding: 0 }}
                  >
                    {sendingObservation ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={generateInitialValues()}
          validationSchema={PrescriptionSchema}
          onSubmit={handleSubmit}
        >
          {formik => {
            return (
              <Form>
                <AutoPreviewWatcher onChange={handlePreview} />
                <FieldsUpdater doctorTitles={doctorTitles} />
                <Typography variant="h6">Datos de la Receta</Typography>
                
                {template?.variables.includes('especialidadMedico') && doctorTitles.length > 1 && (
                  <FormControl fullWidth margin="normal" size="small">
                    <InputLabel>Especialidad del Médico</InputLabel>
                    <Select
                      name="especialidadMedico"
                      value={formik.values.especialidadMedico}
                      onChange={formik.handleChange}
                    >
                      {doctorTitles.map(t => <MenuItem key={t.id} value={t.title}>{t.title}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                
                <Grid container spacing={2}>
                  {template?.variables.filter(v => !['listaMedicamentos', 'firmaMedico', 'especialidadMedico'].includes(v)).map(v => (
                    <Grid item xs={12} key={v}>
                      <Field
                        as={TextField}
                        name={v}
                        label={v.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={formik.touched[v] && Boolean(formik.errors[v])}
                        helperText={formik.touched[v] && formik.errors[v]}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  ))}
                </Grid>

                {template?.variables.includes('listaMedicamentos') && (
                  <FieldArray name="listaMedicamentos">
                    {({ push, remove }) => (
                      <div>
                        {Array.isArray(formik.values.listaMedicamentos) &&
                          formik.values.listaMedicamentos.map((med, index) => {
                            const nameError =
                              formik.touched.listaMedicamentos?.[index]?.name &&
                              formik.errors.listaMedicamentos?.[index]?.name;
                            const doseError =
                              formik.touched.listaMedicamentos?.[index]?.dose &&
                              formik.errors.listaMedicamentos?.[index]?.dose;
                            const instructionsError =
                              formik.touched.listaMedicamentos?.[index]?.instructions &&
                              formik.errors.listaMedicamentos?.[index]?.instructions;

                            return (
                              <Grid container spacing={2} key={index}>
                                <Grid item xs={4}>
                                  <Field
                                    as={TextField}
                                    name={`listaMedicamentos.${index}.name`}
                                    label="Nombre"
                                    fullWidth
                                    size="small"
                                    error={Boolean(nameError)}
                                    helperText={nameError}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <Field
                                    as={TextField}
                                    name={`listaMedicamentos.${index}.dose`}
                                    label="Dosis"
                                    fullWidth
                                    size="small"
                                    error={Boolean(doseError)}
                                    helperText={doseError}
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <Field
                                    as={TextField}
                                    name={`listaMedicamentos.${index}.instructions`}
                                    label="Instrucciones"
                                    fullWidth
                                    size="small"
                                    error={Boolean(instructionsError)}
                                    helperText={instructionsError}
                                  />
                                </Grid>

                                {formik.values.listaMedicamentos.length > 1 && (
                                  <Grid item xs={1}>
                                    <Button onClick={() => remove(index)}>X</Button>
                                  </Grid>
                                )}
                              </Grid>
                            );
                          })}

                        <Box mt={2} mb={1}>
                          <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            size="medium"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => push({ name: "", dose: "", instructions: "" })}
                            style={{ 
                                borderStyle: "dashed", 
                                borderWidth: 2, 
                                fontWeight: "bold",
                                backgroundColor: "rgba(63, 81, 181, 0.04)"
                            }}
                          >
                            AGREGAR MEDICAMENTO
                          </Button>
                        </Box>
                      </div>
                    )}
                  </FieldArray>
                )}
                {template?.variables.includes('listaMedicamentos') && (
                    <Box mt={2}>
                        <Field
                            as={TextField}
                            name="comentarios"
                            label="Indicaciones Adicionales / Comentarios"
                            placeholder="Ej: Beber mucha agua, evitar grasas..."
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                        />
                    </Box>
                )}
                {template?.content?.includes('"Signature"') && (
                  <Box mt={2}>
                    <FormControl component="fieldset">
                      <Typography variant="h6" gutterBottom>Firma del Doctor</Typography>
                      <RadioGroup 
                        row 
                        value={signatureOption} 
                        onChange={(e) => {
                          const value = e.target.value;
                          setSignatureOption(value);
                          formik.setFieldValue("signatureOption", value);
                        }}
                      >
                        <FormControlLabel value="draw" control={<Radio color="primary" />} label="Dibujar ahora" />
                        <FormControlLabel value="saved" control={<Radio color="primary" />} label="Usar firma guardada" disabled={!user.signatureUrl} />
                        <FormControlLabel value="manual" control={<Radio color="primary" />} label="Imprimir y firmar" />
                      </RadioGroup>
                      {!user.signatureUrl && signatureOption === 'saved' && (
                          <FormHelperText error>No tienes una firma guardada. Ve a tu perfil para configurarla.</FormHelperText>
                      )}
                    </FormControl>

                    <Collapse in={signatureOption === 'draw'}>
                      <Box mt={1}>
                        <Box border={formik.touched.signature && formik.errors.signature ? "2px dashed red" : "1px dashed grey"} borderRadius={4} display="inline-block" style={{ backgroundColor: '#fff' }}>
                          <SignatureCanvas
                            ref={sigCanvas}
                            penColor="black"
                            canvasProps={{ width: 400, height: 150, className: "sigCanvas" }}
                            onEnd={() => {
                              const isEmpty = sigCanvas.current.isEmpty();
                              formik.setFieldValue("signature", isEmpty ? "" : `signed-${Date.now()}`);
                              formik.setFieldTouched("signature", true, false);
                            }}
                          />
                        </Box>
                        {formik.touched.signature && formik.errors.signature && (
                          <Typography color="error" variant="caption">{formik.errors.signature}</Typography>
                        )}
                        <Box mt={1}>
                          <Button onClick={() => { sigCanvas.current.clear(); formik.setFieldValue("signature", ""); }} variant="outlined" size="small">Limpiar</Button>
                        </Box>
                      </Box>
                    </Collapse>

                    <Collapse in={signatureOption === 'saved'}>
                      <Box mt={1}>
                        {user.signatureUrl && (
                          <img src={user.signatureUrl} alt="Firma guardada" style={{ width: 400, height: 150, border: '1px solid grey', backgroundColor: '#fff', objectFit: 'contain' }} />
                        )}
                      </Box>
                    </Collapse>

                    <Collapse in={signatureOption === 'manual'}>
                      <Box mt={1}>
                        <Typography color="textSecondary">La receta se guardará sin firma para ser impresa.</Typography>
                      </Box>
                    </Collapse>
                  </Box>
                )}
                {lastAppointment && lastAppointment.status === 'scheduled' && (
                  <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={shouldCompleteAppointment}
                          onChange={(e) => setShouldCompleteAppointment(e.target.checked)}
                          name="completeAppointment"
                          color="primary"
                        />
                      }
                      label="Marcar la cita asociada como 'Finalizada' al enviar la receta."
                    />
                  </Box>
                )}
                <EmptyFieldsWarning />
                <Box mt={2} display="flex" gap={2}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Guardar y Enviar"}
                  </Button>
                </Box>
              </Form>
            )
          }}
        </Formik>
      </Box>

      <Box width="50%" p={2} display="flex" flexDirection="column" height="100%">
        <Typography variant="h6">Previsualización</Typography>
        <Box
          mt={1}
          border={1}
          borderColor="grey.400"
          bgcolor="#fff"
          flexGrow={1}
          overflow="auto"
        >
          {previewLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : offscreenHydratedJson ? (
            <TemplateViewer
              id="template-craft-viewer"
              contentJson={offscreenHydratedJson}
              widthMm={210}
              heightMm={297}
              onReady={handlePreviewReady}
            />
          ) : (
            <Typography>Completa los campos y presiona "Actualizar Previsualización"</Typography>
          )}
        </Box>
      </Box>
      <Dialog 
        open={openDialog} 
        onClose={(_, reason) => { 
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setOpenDialog(false);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>
          Receta enviada con éxito
          <IconButton 
            onClick={() => setOpenDialog(false)} 
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>¿Qué deseas hacer ahora?</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              history.push(`/tickets/${createdTicketId}`);
            }} 
            color="primary" 
            variant="contained"
          >
            Ver en Ticket
          </Button>

          <Button 
            href={createdPdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            Ver Receta (PDF)
          </Button>

          <Button 
            onClick={() => {
              setOpenDialog(false);
              history.push("/calendar");
            }} 
            color="default"
          >
            Volver al Calendario
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewPrescription;